import React from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LayoutDashboard, BriefcaseBusiness, FileText, Info, Settings as SettingsIcon, LogOut, ChevronDown, BarChart3, ShieldCheck } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import Logo from "./Logo";
import { NotificationBell } from "./NotificationBell";

const tabs = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, tid: "tab-dashboard" },
  { to: "/jobs", label: "Jobs", icon: BriefcaseBusiness, tid: "tab-jobs" },
  { to: "/metrics", label: "Metrics", icon: BarChart3, tid: "tab-metrics" },
  { to: "/resume", label: "Resume", icon: FileText, tid: "tab-resume" },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-white text-zinc-900 pb-20 md:pb-0">
      <header className="border-b border-zinc-200 sticky top-0 bg-white z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <Logo size={28} />
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {tabs.map((t) => (
              <NavLink
                key={t.to}
                to={t.to}
                data-testid={t.tid}
                className={({ isActive }) =>
                  "px-3 py-1.5 text-sm font-medium border " +
                  (isActive ? "bg-zinc-900 text-white border-zinc-900" : "border-transparent hover:border-zinc-200")
                }
              >
                {t.label}
              </NavLink>
            ))}
            <NavLink
              to="/about"
              data-testid="tab-about"
              className={({ isActive }) =>
                "px-3 py-1.5 text-sm font-medium border " +
                (isActive ? "bg-zinc-900 text-white border-zinc-900" : "border-transparent hover:border-zinc-200")
              }
            >
              About
            </NavLink>
          </nav>

          <div className="flex items-center gap-1">
            <NotificationBell />
            <DropdownMenu>
            <DropdownMenuTrigger data-testid="user-menu-trigger" className="flex items-center gap-2 px-2 py-1 border border-transparent hover:border-zinc-200 transition-colors">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-7 h-7 border border-zinc-200" />
              ) : (
                <div className="w-7 h-7 bg-zinc-200" />
              )}
              <span className="hidden sm:inline text-sm font-medium max-w-[140px] truncate">{user?.name || "Account"}</span>
              <ChevronDown size={14} className="text-zinc-500" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-none w-56 border-zinc-200">
              <div className="px-3 py-2 text-xs text-zinc-500 truncate">{user?.email}</div>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild data-testid="menu-about">
                <Link to="/about" className="cursor-pointer">
                  <Info size={14} className="mr-2" /> About
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild data-testid="menu-settings">
                <Link to="/settings" className="cursor-pointer">
                  <SettingsIcon size={14} className="mr-2" /> Settings
                </Link>
              </DropdownMenuItem>
              {user?.is_admin && (
                <DropdownMenuItem asChild data-testid="menu-admin">
                  <Link to="/admin" className="cursor-pointer">
                    <ShieldCheck size={14} className="mr-2" /> Admin stats
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout" className="cursor-pointer">
                <LogOut size={14} className="mr-2" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <Outlet />
      </main>

      {/* Support footer */}
      <footer className="hidden md:block border-t border-zinc-200 mt-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-12 flex items-center justify-center">
          <a
            href="https://ko-fi.com/suddengenesis"
            target="_blank" rel="noopener noreferrer"
            data-testid="app-footer-kofi-link"
            className="label-tiny text-zinc-600 hover:text-zinc-900 transition-colors"
          >
            ☕ Enjoying ReInVocation? Help fund the site
          </a>
        </div>
      </footer>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-zinc-200 z-30">
        <div className="grid grid-cols-4">
          {tabs.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              data-testid={`m-${t.tid}`}
              className={({ isActive }) =>
                "flex flex-col items-center justify-center py-2 text-[10px] uppercase tracking-widest " +
                (isActive ? "text-zinc-900" : "text-zinc-400")
              }
            >
              <t.icon size={20} strokeWidth={1.5} />
              <span className="mt-1">{t.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
